import type { Cart, CalculationResult, CheckoutResponseData } from '../types';

export interface LoyaltyInfo {
  tier: string | null;
  points: number | null;
}

export interface CustomerInfo {
  tags: string[];
  loyalty: LoyaltyInfo | null;
  hasPhone: boolean;
}

export interface ApiError {
  message: string;
  status?: number;
}

/**
 * Fetch customer info (tags + loyalty) from backend API
 * @param apiUrl API base URL
 * @param customerId Customer ID
 * @param bypassCache If true, bypasses sessionStorage cache and fetches fresh data
 */
export async function fetchCustomerInfo(
  apiUrl: string,
  customerId: string,
  bypassCache: boolean = false
): Promise<CustomerInfo> {
  try {
    const cacheKey = `customer_info_${customerId}`;

    // Clear cache if bypassing
    if (bypassCache) {
      sessionStorage.removeItem(cacheKey);
    } else {
      const cached = sessionStorage.getItem(cacheKey);

      console.log('Fetching customer info for ID:', customerId, cached);

      if (cached) {
        const { info, timestamp } = JSON.parse(cached);
        // Cache for 5 minutes
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return info;
        }
      }
    }

    // Add skipCache query parameter to bypass server-side cache when bypassCache is true
    const url = bypassCache 
      ? `${apiUrl}/customers/${customerId}?skipCache=true`
      : `${apiUrl}/customers/${customerId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch customer tags: ${response.statusText}`);
    }

    const data = await response.json();
    const info: CustomerInfo = {
      tags: data?.tags || data?.data?.tags || [],
      loyalty: data?.data?.loyalty || null,
      hasPhone: data?.data?.hasPhone ?? false,
    };

    console.log('Fetched customer info:', info);

    // Cache the result
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({ info, timestamp: Date.now() })
    );

    return info;
  } catch (error) {
    console.error('Error fetching customer info:', error);
    // Return empty structure on error (graceful degradation)
    return {
      tags: [],
      loyalty: null,
      hasPhone: false,
    };
  }
}

/**
 * Send OTP code to phone number via WhatsApp
 */
export async function sendPhoneOTP(
  apiUrl: string,
  phone: string,
  customerId: string
): Promise<void> {
  try {
    const response = await fetch(`${apiUrl}/phone-verification/send-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone,
        customerId,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send OTP code');
    }
  } catch (error) {
    console.error('Error sending phone OTP:', error);
    throw error;
  }
}

/**
 * Verify OTP code and link phone number to customer
 */
export async function verifyPhoneOTP(
  apiUrl: string,
  phone: string,
  customerId: string,
  code: string
): Promise<{ merged: boolean }> {
  try {
    const response = await fetch(`${apiUrl}/phone-verification/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone,
        customerId,
        code,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to verify OTP code');
    }

    return {
      merged: data.merged || false,
    };
  } catch (error) {
    console.error('Error verifying phone OTP:', error);
    throw error;
  }
}

/**
 * Calculate discounts for a cart
 */
export async function calculateDiscounts(
  apiUrl: string,
  cart: Cart
): Promise<CalculationResult> {
  try {
    const response = await fetch(`${apiUrl}/discounts/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ cart }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to calculate discounts: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Discount calculation failed');
    }

    return data.data;
  } catch (error) {
    console.error('Error calculating discounts:', error);
    throw error;
  }
}

interface CheckoutRequestPayload {
  cart: Cart;
  customer?: {
    id?: string;
    email?: string;
  };
  shipping_address?: Record<string, unknown>;
  billing_address?: Record<string, unknown>;
  note?: string;
  tags?: string[];
  metafields?: Array<{
    namespace: string;
    key: string;
    value: string;
    type?: string;
  }>;
  currency?: string;
  live_session_id?: string;
}

export interface CheckoutResult {
  draftOrder: CheckoutResponseData;
  calculation: CalculationResult;
}

export async function createCheckout(
  apiUrl: string,
  payload: CheckoutRequestPayload
): Promise<CheckoutResult> {
  try {
    const response = await fetch(`${apiUrl}/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      throw new Error(
        data?.message || `Failed to create checkout: ${response.statusText}`
      );
    }

    return {
      draftOrder: data.data as CheckoutResponseData,
      calculation: data.calculation as CalculationResult,
    };
  } catch (error) {
    console.error('Error creating checkout:', error);
    throw error;
  }
}

/**
 * Fetch Shopify cart from /cart.js endpoint
 */
export async function fetchShopifyCart(): Promise<any> {
  try {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching Shopify cart:', error);
    throw error;
  }
}

